import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import type {
  ResourceModelConfig,
  EventModelConfig,
} from '@bryntum/scheduler-thin';

import {
  useColleaguesCalendarSchedulersQuery,
  useColleaguesCalendarTargetsQuery,
  useColleaguesCalendarShiftsDataQuery,
} from '../../../__generated__/graphql';
import {
  type ViewType,
  VIEW_PARAM,
  DATE_PARAM,
  SCHEDULER_PARAM,
} from './types';
import { format } from 'date-fns';
import { getDateRangeForView, parsePageQueryParams } from './utils';
import { useToasts } from '../../../../../context/Toast';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';

export type SchedulerOption = {
  Id: string;
  Name: string;
};

export type TargetOption = {
  id: string;
  name: string;
};

export function useColleaguesCalendarData() {
  const history = useHistory();
  const { addError } = useToasts();
  const t = useNamespacedTranslation('schedulerShifts.colleaguesCalendar');

  const initialParams = useRef(
    parsePageQueryParams(new URLSearchParams(history.location.search)),
  ).current;

  const [view, setView] = useState<ViewType>(initialParams.view);
  const [currentDate, setCurrentDate] = useState<Date>(initialParams.date);
  const [selectedScheduler, setSelectedScheduler] =
    useState<SchedulerOption | null>(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[] | null>(
    null,
  );

  const {
    data: schedulersData,
    loading: schedulersLoading,
    error: schedulersError,
  } = useColleaguesCalendarSchedulersQuery();

  const schedulers: SchedulerOption[] = useMemo(
    () =>
      (schedulersData?.me?.schedulers ?? []).map((s) => ({
        Id: s.Id,
        Name: s.Name,
      })),
    [schedulersData?.me?.schedulers],
  );

  const effectiveScheduler = useMemo(() => {
    if (
      selectedScheduler &&
      schedulers.some((s) => s.Id === selectedScheduler.Id)
    )
      return selectedScheduler;
    if (initialParams.schedulerId) {
      const fromUrl = schedulers.find(
        (s) => s.Id === initialParams.schedulerId,
      );
      if (fromUrl) return fromUrl;
    }
    return schedulers[0] ?? null;
  }, [selectedScheduler, initialParams.schedulerId, schedulers]);

  const {
    data: targetsData,
    loading: targetsLoading,
    error: targetsError,
  } = useColleaguesCalendarTargetsQuery({
    variables: { schedulerId: effectiveScheduler?.Id ?? '' },
    skip: !effectiveScheduler?.Id,
  });

  const allTargets: TargetOption[] = useMemo(
    () =>
      (targetsData?.me?.employeeSchedulerTargets?.allTargets ?? []).map(
        (t) => ({ id: t.id, name: t.name }),
      ),
    [targetsData?.me?.employeeSchedulerTargets?.allTargets],
  );

  const employeeTargetIds: string[] = useMemo(
    () =>
      (targetsData?.me?.employeeSchedulerTargets?.employeeTargets ?? []).map(
        (t) => t.id,
      ),
    [targetsData?.me?.employeeSchedulerTargets?.employeeTargets],
  );

  // Derive effective target IDs: user selection takes priority, otherwise default to employee targets
  const effectiveTargetIds = selectedTargetIds ?? employeeTargetIds;

  const { startDate, endDate } = useMemo(
    () => getDateRangeForView(currentDate, view),
    [currentDate, view],
  );

  const {
    data: shiftsData,
    loading: shiftsLoading,
    error: shiftsError,
  } = useColleaguesCalendarShiftsDataQuery({
    variables: {
      input: {
        schedulerId: effectiveScheduler?.Id ?? '',
        targetIds: effectiveTargetIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    },
    skip:
      !effectiveScheduler?.Id ||
      targetsLoading ||
      effectiveTargetIds.length === 0 ||
      !startDate ||
      !endDate,
  });

  useEffect(() => {
    if (schedulersError || targetsError || shiftsError) {
      addError(t('errorLoadingData'));
    }
  }, [schedulersError, targetsError, shiftsError, addError, t]);

  const resources: ResourceModelConfig[] = useMemo(() => {
    if (!shiftsData?.me?.schedulerShiftsData?.employees) return [];
    return shiftsData.me.schedulerShiftsData.employees.map((employee) => ({
      id: employee.Id,
      name:
        employee.flair__First_Name__c && employee.flair__Last_Name__c
          ? `${employee.flair__First_Name__c} ${employee.flair__Last_Name__c}`
          : employee.Name,
      avatarUrl: employee.avatar?.url ?? null,
    }));
  }, [shiftsData?.me?.schedulerShiftsData?.employees]);

  const targetNameMap: Record<string, string> = useMemo(
    () => Object.fromEntries(allTargets.map((t) => [t.id, t.name])),
    [allTargets],
  );

  const events: EventModelConfig[] = useMemo(() => {
    if (!shiftsData?.me?.schedulerShiftsData?.shifts) return [];
    return shiftsData.me.schedulerShiftsData.shifts.map((shift) => ({
      id: shift.Id,
      name: shift.flair__Title__c || '',
      startDate: new Date(shift.flair__Start_Datetime__c),
      endDate: new Date(shift.flair__End_Datetime__c),
      resourceId: shift.flair__Resource_Employee__c ?? '',
      eventColor: shift.flair__Shift_Color__c,
      eventStyle: 'border',
      targetName: shift.flair__Linked_Target_Id__c
        ? targetNameMap[shift.flair__Linked_Target_Id__c] ?? null
        : null,
    }));
  }, [shiftsData?.me?.schedulerShiftsData?.shifts, targetNameMap]);

  const initialLoading =
    schedulersLoading || (!!effectiveScheduler?.Id && targetsLoading);
  const dataLoading = shiftsLoading;

  const updateUrl = useCallback(
    (newView: ViewType, newDate: Date, newSchedulerId?: string | null) => {
      const params = new URLSearchParams(history.location.search);
      const newDateStr = format(newDate, 'yyyy-MM-dd');
      const currentViewParam = params.get(VIEW_PARAM);
      const currentDateParam = params.get(DATE_PARAM);
      const currentScheduler = params.get(SCHEDULER_PARAM);
      const schedulerId =
        newSchedulerId !== undefined
          ? newSchedulerId
          : effectiveScheduler?.Id ?? currentScheduler;
      if (
        currentViewParam === newView &&
        currentDateParam === newDateStr &&
        currentScheduler === (schedulerId ?? '')
      ) {
        return;
      }
      params.set(VIEW_PARAM, newView);
      params.set(DATE_PARAM, newDateStr);
      if (schedulerId) {
        params.set(SCHEDULER_PARAM, schedulerId);
      } else {
        params.delete(SCHEDULER_PARAM);
      }
      history.replace({ search: params.toString() });
    },
    [history, effectiveScheduler?.Id],
  );

  const handleSchedulerChange = useCallback(
    (scheduler: SchedulerOption | null) => {
      setSelectedScheduler(scheduler);
      setSelectedTargetIds(null);
      updateUrl(view, currentDate, scheduler?.Id ?? null);
    },
    [view, currentDate, updateUrl],
  );

  const handleTargetsChange = useCallback((targetIds: string[]) => {
    setSelectedTargetIds(targetIds);
  }, []);

  const handleViewChange = useCallback(
    (newView: ViewType) => {
      setView(newView);
      updateUrl(newView, currentDate);
    },
    [currentDate, updateUrl],
  );

  const handleDateChange = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate);
      updateUrl(view, newDate);
    },
    [view, updateUrl],
  );

  return {
    view,
    currentDate,
    startDate,
    endDate,
    schedulers,
    effectiveScheduler,
    allTargets,
    effectiveTargetIds,
    resources,
    events,
    initialLoading,
    dataLoading,
    handleSchedulerChange,
    handleTargetsChange,
    handleViewChange,
    handleDateChange,
  };
}
